<template>
	<div
		class="header-bottom prevent-highlight-mobile"
		:class="{ 'header-bottom--sidebar-open': isSidebarVisible }"
		:style="{ top: `${headerHeight}px` }"
	>
		<div v-click-outside="clickOutsideHandler" class="header-bottom__drop-nav">
			<burger-button @toggle-sidebar="toggleSidebar" />
			<div class="header-bottom__drop-nav__category-holder">
				<category-list
					@nav-item-click="onNavItemClick($event.category, $event.index)"
					class="header-bottom__drop-nav__category-list"
				/>
				<div
					v-if="isSidebarVisible"
					@click="hideMenuSidebar"
					class="header-bottom__drop-nav__overlay"
				></div>
			</div>
		</div>

		<nav class="header-bottom__main-nav">
			<div
				class="search-icon search-icon--mobile"
				@click="openMobileSearch(true)"
			>
				<icon-svg icon="ic_search" :width="28" />
			</div>
			<div v-if="notEmptyCategories.length > 0" class="nav-scroll" ref="nav">
				<span
					v-for="(category, index) in notEmptyCategories"
					:key="`${category.name}_${index}`"
					class="nav-scroll__item"
					:class="{ active: $route.params.category === category.name }"
					ref="navItems"
					@click="onNavItemClick(category, index)"
				>
					{{ category.name }}
				</span>
				<div class="active-line" :style="lineStyle"></div>
			</div>
		</nav>
		<div
			class="header-bottom__search-wrapper"
			:class="{
				'header-bottom__search-wrapper--visible': isMobileSearchVisible,
				'header-bottom__search-wrapper--focused': isInputFocused
			}"
		>
			<div class="search-icon">
				<icon-svg icon="ic_search" :width="28" />
			</div>
			<search-field
				ref="searchInput"
				type="text"
				:placeholder="`Search ${venueInfo.name || ''}`"
				:value="productSearchValue"
				:search-value-highlighted="searchValueHighlighted"
				:is-show-close-button-by-default="isMobileSearchVisible"
				@input="changeSearch"
				@clear="openMobileSearch(false)"
				@input-focused="isInputFocused = true"
				@input-blurred="isInputFocused = false"
			/>
		</div>
	</div>
</template>

<script>
import BurgerButton from '@/components/Buttons/BurgerButton.vue'
import CategoryList from '@/components/CategoryList.vue'
import SearchField from '@/components/forms/SearchField.vue'

import IconSvg from '@/components/IconSvg.vue'

import { clickOutside } from '@/utils/helpers'
import { mapActions, mapGetters, mapState } from 'vuex'

const SCROLL_DIST = 100

export default {
	name: 'MainNavigation',

	props: {
		disableAutoScroll: {
			type: Boolean,
			default: false
		}
	},

	directives: {
		'click-outside': clickOutside
	},

	components: {
		SearchField,
		IconSvg,
		CategoryList,
		BurgerButton
	},

	data() {
		return {
			search: '',
			headerHeight: 0,
			isMobileSearchVisible: false,
			isInputFocused: false,

			lineStyle: {
				width: '0px',
				left: '0px'
			}
		}
	},

	mounted() {
		this.$nextTick(() => {
			this.updateHeaderHeight()
			window.addEventListener('scroll', this.updateHeaderHeight)
			window.addEventListener('resize', this.updateHeaderHeight)
		})
		if (this.category) {
			setTimeout(() => this.scrollActiveIntoView(), 50)
		}
	},

	watch: {
		category(value) {
			value && this.scrollActiveIntoView()
		},
		notEmptyCategories: {
			handler() {
				this.$nextTick(() => this.scrollActiveIntoView())
			},
			deep: true
		},
		filter(val) {
			// Not for production: Expo temporary guard to prevent category auto-scroll
			// while Products keeps scroll-spy disabled during footer redirect flow
			if (this.disableAutoScroll) {
				return
			}

			if (val == null) {
				this.scrollToFirstVisibleCategory()
				return
			}
			const first =
				this.firstNonEmptyCategoryName || this.$route.params.category
			this.startNavAutoScroll()
			this.replaceRouteIfChanged({
				name: this.$route.name,
				params: {
					...this.$route.params,
					category: first
				},
				query: this.$route.query
			})
			this.scrollToFirstVisibleCategory()
		}
	},

	computed: {
		...mapState('venue', ['venueInfo']),
		...mapState('ui', [
			'isSidebarVisible',
			'isDesktopDevice',
			'isIOS',
			'isChrome'
		]),
		...mapGetters('productCatalog', [
			'notEmptyCategories',
			'isEmptySearchResult',
			'productSearchValue',
			'filter'
		]),

		searchValueHighlighted() {
			if (this.isEmptySearchResult && this.productSearchValue) {
				const { productSearchValue } = this
				const lastPart = productSearchValue[productSearchValue.length - 1]
				const firstPart = productSearchValue.slice(0, -1)
				return `${firstPart
					.replace(/</g, '&lt;')
					.replace(
						/>/g,
						'&gt;'
					)}<span class="highlighted-text">${lastPart
					.replace(/</g, '&lt;')
					.replace(/>/g, '&gt;')}</span>`
			}

			return ''
		},

		category() {
			return this.$route.params.category
		},

		scrollBehavior() {
			// Avoid content cut bug in Ios Chrome
			return { behavior: this.isChrome && this.isIOS ? 'auto' : 'smooth' }
		},

		firstNonEmptyCategoryName() {
			const categories = this.notEmptyCategories || []
			return categories.length > 0 ? categories[0].name : null
		}
	},

	beforeDestroy() {
		window.removeEventListener('scroll', this.updateHeaderHeight)
		window.removeEventListener('resize', this.updateHeaderHeight)
	},

	methods: {
		...mapActions('ui', [
			'toggleSidebarVisibility',
			'hideSidebar',
			'startNavAutoScroll',
			'lockScroll',
			'unlockScroll'
		]),
		...mapActions('productCatalog', ['changeProductSearch']),

		toggleSidebar() {
			if (!this.isDesktopDevice) {
				if (this.isSidebarVisible) {
					this.unlockScroll()
				} else {
					this.lockScroll()
				}
			}

			this.toggleSidebarVisibility()
		},

		hideMenuSidebar() {
			if (!this.isDesktopDevice && this.isSidebarVisible) {
				this.unlockScroll()
			}

			this.hideSidebar()
		},

		clickOutsideHandler() {
			if (this.isDesktopDevice) {
				this.hideSidebar()
			}
		},

		changeSearch(val) {
			this.changeProductSearch(val)
			const first =
				this.firstNonEmptyCategoryName || this.$route.params.category
			if (val != null) {
				this.startNavAutoScroll()
				this.replaceRouteIfChanged({
					name: this.$route.name,
					params: {
						...this.$route.params,
						category: first || this.$route.params.category
					},
					query: { filter: 'all', trending: 0 }
				})
			}
			// scroll to first visible category after search change
			this.scrollToFirstVisibleCategory()
		},

		updateHeaderHeight() {
			const header = document.querySelector('.header')
			if (header) {
				this.headerHeight = header.getBoundingClientRect().height
			}
		},

		scrollToCatDefault(el) {
			const offset = this.getScrollOffset()
			const top =
				el.getBoundingClientRect().top + (window.pageYOffset || 0) - offset
			window.scrollTo({ top, ...this.scrollBehavior })
		},

		afterNextFrame(callback) {
			requestAnimationFrame(() => requestAnimationFrame(callback))
		},

		scrollToCatIOS(el) {
			const offset = this.getScrollOffset()
			const startY = window.pageYOffset || 0
			const baseY = el.getBoundingClientRect().top + startY - offset
			const scrollDiff = baseY - startY
			if (scrollDiff <= 0) {
				this.scrollToCatDefault(el)
				return
			}
			window.scrollBy({
				top: Math.min(scrollDiff, SCROLL_DIST),
				left: 0,
				behavior: 'auto'
			})
			this.afterNextFrame(() => {
				const top =
					el.getBoundingClientRect().top + (window.pageYOffset || 0) - offset
				window.scrollTo({ top, ...this.scrollBehavior })
			})
		},

		getScrollOffset() {
			const fallbackHeight = 160
			const extraIndent = 20
			const headerHeight = this.getHeight('.header')
			const headerBottomHeight = this.getHeight('.header-bottom')
			return extraIndent + headerHeight + headerBottomHeight || fallbackHeight
		},

		onNavItemClick(category, index) {
			const el = document.getElementById(`cat-${category.name}`)
			if (!el) return
			this.startNavAutoScroll()
			// useIOSFlow handles iOS scroll near the top banner (app install bar)
			// If iOS and within SCROLL_DIST from top use special scroll, else use default.
			const useIOSFlow = this.isIOS && (window.pageYOffset || 0) < SCROLL_DIST
			useIOSFlow ? this.scrollToCatIOS(el) : this.scrollToCatDefault(el)
			this.replaceRouteIfChanged({
				name: 'products-category',
				params: { category: category.name },
				query: { ...this.$route.query }
			})
			this.scrollNavToIndex(index)
			this.hideSidebar()
		},

		getHeight(selector) {
			const el = document.querySelector(selector)
			return el ? Math.round(el.getBoundingClientRect().height) : 0
		},

		scrollActiveIntoView() {
			this.$nextTick(() => {
				const items = this.$refs.navItems || []
				if (!items.length) {
					return false
				}
				const activeIndex = this.notEmptyCategories.findIndex(
					({ name }) => name === this.$route.params.category
				)
				if (activeIndex >= 0) {
					this.scrollNavToIndex(activeIndex)
				}
			})
		},

		scrollNavToIndex(index) {
			const nav = this.$refs.nav
			const navItems = this.$refs.navItems || []
			const activeCatEl = navItems[index]

			if (!nav || !activeCatEl) {
				return false
			}

			const currOffset = activeCatEl.offsetLeft - this.getPaddingLeftPx(nav)
			const maxOffset = nav.scrollWidth - nav.clientWidth

			this.lineStyle = {
				width: `${activeCatEl.offsetWidth}px`,
				left: `${activeCatEl.offsetLeft}px`
			}

			nav.scrollTo({ left: this.limitToRange(currOffset, maxOffset) })
		},

		getPaddingLeftPx(el) {
			const { paddingLeft } = getComputedStyle(el)
			const paddingLeftPx = Number.parseFloat(paddingLeft)
			return Number.isNaN(paddingLeftPx) ? 0 : paddingLeftPx
		},

		limitToRange(value, max) {
			return Math.min(max, Math.max(0, value))
		},

		openMobileSearch(condition = true) {
			const searchInput = this.$refs.searchInput
			if (searchInput && condition) {
				searchInput.focusInput()
			}
			this.isMobileSearchVisible = condition
		},

		replaceRouteIfChanged(targetRoute) {
			const resolved = this.$router.resolve(targetRoute)
			const isSame = resolved.href === this.$route.fullPath
			if (!isSame) {
				this.$router.replace(targetRoute)
			}
		},

		scrollToFirstVisibleCategory() {
			const first = this.firstNonEmptyCategoryName
			if (!first) return
			this.$nextTick(() => {
				const el = document.getElementById(`cat-${first}`)
				if (!el) return
				const useIOS = this.isIOS && (window.pageYOffset || 0) < SCROLL_DIST
				useIOS ? this.scrollToCatIOS(el) : this.scrollToCatDefault(el)
			})
		}
	}
}
</script>

<style lang="scss">
@import './src/assets/styles/variables';

$searchWidth: 291px;
$searchWidthIpad: 220px;
$navOpenerWidth: 40px;

.header {
	&.active-search {
		background: transparent;
		background-image: none !important;

		&:before {
			display: none;
		}
	}
}

.header-bottom {
	position: sticky;
	top: 0;
	z-index: 103;
	display: flex;
	align-items: center;
	padding: 6px 0 0 5px;
	background-color: $background-dark;
	border-bottom: 1px solid $border-color;

	@media (max-width: 767px) {
		padding: 0 0 0 16px;
		background-color: $dark-violet;
		margin: 0 -16px;
	}

	&--sidebar-open {
		z-index: 103;

		.header-bottom__drop-nav__category-list {
			visibility: visible;
			opacity: 1;

			@media (max-width: 1024px) {
				transform: translateY(0);
			}
		}
	}

	&__drop-nav {
		position: relative;
		width: $navOpenerWidth;
		height: $navOpenerWidth;

		&__category-list {
			position: absolute;
			top: calc(100% + 25px);
			left: -5px;
			width: 271px;
			height: auto;
			visibility: hidden;
			opacity: 0;
			transition: visible 0.3s, opacity 0.3s;

			@media (max-width: 1024px) {
				position: fixed;
				top: auto;
				right: 0;
				bottom: 0;
				left: 0;
				width: auto;
				height: auto;
				max-height: 100dvh;
				min-height: 50dvh;
				margin: 0;
				visibility: visible;
				opacity: 1;
				transition: transform 0.3s;
				transform: translateY(100%);
				z-index: 105;
			}
		}

		&__overlay {
			display: none;
			position: fixed;
			top: 0;
			left: 0;
			width: 100%;
			height: 100dvh;
			background: rgba(0, 0, 0, 0.8);
			z-index: 104;
			pointer-events: auto;
			@media (max-width: 1023px) {
				display: block;
			}
		}
	}

	&__main-nav {
		position: relative;
		display: flex;
		align-items: center;
		width: calc(100% - #{$searchWidth} - #{$navOpenerWidth});
		padding: 0 24px 0 10px;
		font-size: 12px;

		@media (max-width: 1023px) {
			width: calc(100% - #{$searchWidthIpad} - #{$navOpenerWidth});
			padding-left: 0;
		}

		@media (max-width: 767px) {
			width: calc(100% - #{$navOpenerWidth});
			padding-right: 15px;
		}
	}
	&__search-wrapper {
		position: relative;
		width: $searchWidth;
		padding: 3px 6px 3px 42px;
		border: 1px solid $border-color;
		border-radius: 32px;
		background-color: $deep-violet;
		margin: 9px 0;

		@media (max-width: 1023px) {
			width: $searchWidthIpad;
		}

		@media (max-width: 767px) {
			position: absolute;
			width: 100%;
			top: 2px;
			left: 0;
			transform: translateX(-150%);
			bottom: -1px;
			margin: 0;
			border-radius: 0;
			border: none;
			border-bottom: 1px solid $border-color;
			z-index: 100;
			padding: 0 6px 0 42px;
			transition: transform 0.2s ease-in, border-color 0.2s;

			&--visible {
				transform: translateX(0);
			}
			&--focused {
				border-bottom-color: $light-purple;
			}
		}

		.search-field input {
			@media (max-width: 767px) {
				padding: 8px 10px 8px 5px;
			}
		}

		.input-field.search-field .search-highlighter {
			@media (max-width: 767px) {
				bottom: 8px;
			}
		}

		.search-icon {
			position: absolute;
			top: 5px;
			left: 1px;
			z-index: 2;
			display: flex;
			align-items: center;
			justify-content: center;
			width: 40px;
			height: 40px;
			opacity: 0.5;
			transform: scale(0.8);

			@media (max-width: 767px) {
				top: -1px;
				opacity: 1;
				&::after,
				&::before {
					height: 1.2px;
				}
			}

			&.left {
				left: 49px;
				bottom: -6px;
				opacity: 0.5;
				cursor: default;
			}
		}

		.search {
			margin-left: 44px;
		}
	}
	.nav-scroll {
		position: relative;
		display: inline-flex;
		align-items: center;
		width: 100%;
		gap: 34px;
		overflow-x: auto;
		overflow-y: hidden;
		white-space: nowrap;
		-webkit-overflow-scrolling: touch;
		scrollbar-width: none;
		scroll-behavior: smooth;
	}

	.active-line {
		position: absolute;
		bottom: 0;
		height: 3px;
		background-color: $white;
		transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
		pointer-events: none;
	}

	.nav-scroll::-webkit-scrollbar {
		display: none;
	}

	.nav-scroll__item {
		position: relative;
		display: inline-block;
		flex-shrink: 0;
		max-width: 100%;
		padding: 24px 2px 24px;
		margin: 0;
		border: 0;
		background: transparent;
		font-size: 16px;
		color: $white;
		text-transform: capitalize;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		opacity: 1;
		cursor: pointer;
		transition: transform 0.2s, opacity 0.2s, border-color 0.2s, color 0.3s;

		@media (max-width: 1023px) {
			padding: 20px 2px 20px;
		}
		@media (max-width: 767px) {
			padding: 10px 2px;
		}

		@media (min-width: 1024px) {
			&:hover:not(.active) {
				color: $blue;
			}
		}
	}

	.search-icon--mobile {
		margin-right: 5px;
		margin-top: 3px;
		width: 40px;
		height: 40px;
		display: inline-flex;
		justify-content: center;
		align-items: center;
		transform: scale(0.8);

		@media (min-width: 768px) {
			display: none;
		}
	}
}

.menu-title {
	margin: 0 106px 10px;
	font-size: 20px;
	font-weight: 700;
	color: $white;
	@media (max-width: 767px) {
		font-size: 16px;
	}
}

.menu-subtitle {
	display: block;
	padding: 0 0 20px;
	font-size: 12px;
	color: rgba($white, 0.5);
}

.menu-button {
	@media (max-width: 767px) {
		background-color: transparent;
	}
}

.main-nav-holder {
	@media (max-width: 767px) {
		padding: 2px 0;
	}
}

.main-nav {
	@media (max-width: 767px) {
		.swiper-container {
			padding: 0 22px;
		}
		.search-wrapper {
			margin-right: 16px;

			.input-field {
				margin-left: 48px;
			}
		}
		.search-icon {
			right: 10px;
		}
		a:hover,
		a.router-link-active,
		a.active {
			transform: scale(1.2);
		}
	}
}
</style>
